// src/components/Control/Features/VisionPanel.tsx
// «Что видит робот» (нода vision на Pi): кто в кадре (в т.ч. знакомые лица),
// смена позы (сел/встал/упал/руки вверх), знаки (QR, ArUco, «Стоп»), предметы.
// События показываются текстом и по выбору озвучиваются в браузере и/или на роботе
// (озвучку, писк и уведомления делает система событий страницы — на любом экране).
//   robot/<id>/vision/state, /events  <- настройки/возможности, события
//   robot/<id>/vision/cmd             -> {"cmd": "config"|"describe"|"enroll"|"forget_face"|
//                                         "describe_map"|"objects_clear"|"object_forget"}
//   robot/<id>/vision/objects         <- предметы, отмеченные на карте
import React, { useState } from 'react';
import { putJson, useZenohJson } from '../../../hooks/useZenohSubscription';
import { useTick } from '../../../hooks/useTick';
import { speak, speechSupported } from '../../../audio/pcm';
import { MapObject, MapObjectsMsg, objectIcon } from '../objectIcons';
import { FeatureProps, VisionConfig, isStale, loadPref, savePref, timeOf } from '../../../types/features';
import { useEventsState, useTelemetryState } from '../robot/RobotContext';

const TOPICS: [keyof VisionConfig, string][] = [
  ['people', 'люди в кадре'],
  ['faces', 'знакомые лица'],
  ['pose', 'поза (сел, встал, упал, руки)'],
  ['signs', 'знаки, QR-коды, маркеры'],
  ['objects', 'предметы и животные'],
];

const BACKEND_RU: Record<string, string> = {
  HailoYolo: 'Hailo-8L', OnnxYolo: 'CPU (ONNX)', FaceId: 'YuNet+SFace',
};

// part: какую часть показывать (раздел настроек «Люди и распознавание» или «Карты»)
const VisionPanel: React.FC<FeatureProps & { part?: 'all' | 'recognition' | 'map' }> = ({ zenohSession, robotId, part = 'all' }) => {
  const prefix = `robot/${robotId}/vision`;
  const state = useTelemetryState(t => t.vision);
  const events = useEventsState(e => e.events.filter(ev => ev.source === 'vision').slice(0, 50));
  const [speakBrowser, setSpeakBrowser] = useState(() => loadPref('vision.speakBrowser', false));
  const [beepOn, setBeepOn] = useState(() => loadPref('vision.beep', true));
  const [faceName, setFaceName] = useState('');
  useTick(2000);

  const [mapObjects, setMapObjects] = useState<MapObject[]>([]);
  useZenohJson<MapObjectsMsg>(zenohSession, `${prefix}/objects`, msg => setMapObjects(msg.objects ?? []));

  const online = !!state && !isStale(state.timestamp);
  const cfg = state?.config;
  const setConfig = (patch: Partial<VisionConfig>) => putJson(zenohSession, `${prefix}/cmd`, { cmd: 'config', ...patch });
  const backends = state?.backends ?? {};
  const classes = state?.classes ?? {};
  const chosen = new Set(cfg?.classes ?? []);

  const mapChosen = new Set(cfg?.map_classes ?? []);
  const toggleMapClass = (label: string, on: boolean) => {
    const next = new Set(mapChosen);
    if (on) next.add(label);
    else next.delete(label);
    setConfig({ map_classes: Array.from(next) });
  };

  const toggleClass = (label: string, on: boolean) => {
    const next = new Set(chosen);
    if (on) next.add(label);
    else next.delete(label);
    setConfig({ classes: Array.from(next) });
  };

  const enroll = () => {
    const name = faceName.trim();
    if (!name) return;
    putJson(zenohSession, `${prefix}/cmd`, { cmd: 'enroll', name });
    setFaceName('');
  };

  const backendBadge = (key: string, label: string) => {
    const v = backends[key];
    const ok = !!v;
    return (
      <span key={key} className={`badge ${ok ? 'ok' : 'off'}`} title={ok ? String(v) : 'не установлено на роботе'}>
        {label}{typeof v === 'string' ? `: ${BACKEND_RU[v] ?? v}` : ''}
      </span>
    );
  };

  const header = (
    <h3>
      <span>👁 Что видит робот</span>
      {online ? (
        <label className={`badge ${cfg?.enabled ? 'ok' : ''}`} style={{ fontSize: '0.85rem' }}>
          <input type="checkbox" checked={!!cfg?.enabled} onChange={e => setConfig({ enabled: e.target.checked })} />
          {cfg?.enabled ? `включено · ${state!.fps} к/с` : 'выключено'}
        </label>
      ) : (
        <span className="badge off">нет данных</span>
      )}
    </h3>
  );

  const mapPart = (
    <>
      <h4>Карта предметов</h4>
      <div className="row">
        <label>
          <input type="checkbox" checked={!!cfg?.map_objects} disabled={!online} onChange={e => setConfig({ map_objects: e.target.checked })} />
          Отмечать предметы на карте при проезде
        </label>
      </div>
      <details>
        <summary>Какие предметы отмечать (выбрано {mapChosen.size})</summary>
        <div className="class-grid">
          {Object.entries(classes).filter(([label]) => label !== 'person').map(([label, ru]) => (
            <label key={label}>
              <input type="checkbox" checked={mapChosen.has(label)} onChange={e => toggleMapClass(label, e.target.checked)} />
              {objectIcon(label)} {ru}
            </label>
          ))}
        </div>
      </details>
      <div className="row">
        <button type="button" className="btn-sm primary" disabled={!online}
          onClick={() => putJson(zenohSession, `${prefix}/cmd`, { cmd: 'describe_map', speak: !!cfg?.speak })}>
          Что на карте?
        </button>
        <button type="button" className="btn-sm danger" disabled={!online || mapObjects.length === 0}
          onClick={() => window.confirm('Убрать с карты все отмеченные предметы? (например, для новой карты)')
            && putJson(zenohSession, `${prefix}/cmd`, { cmd: 'objects_clear' })}>
          Очистить
        </button>
        <span className="hint">клавиша M — рассказать вслух</span>
      </div>
      {mapObjects.length === 0 ? (
        <p className="hint">Пока ничего не отмечено. Проедьте по комнатам — предмет появится на карте, когда робот увидит его 3 раза.</p>
      ) : (
        <ul className="event-list">
          {mapObjects.map(o => (
            <li key={o.id}>
              <span>{objectIcon(o.label)}</span>
              <span className="text">{o.label_ru}{o.part ? ` — в ${o.part}` : ''} <span className="hint">({o.x.toFixed(1)}, {o.y.toFixed(1)} м · {o.hits}×)</span></span>
              <button type="button" className="btn-icon" title="Убрать с карты" aria-label={`Убрать с карты: ${o.label_ru}`}
                onClick={() => putJson(zenohSession, `${prefix}/cmd`, { cmd: 'object_forget', id: o.id })}>✕</button>
            </li>
          ))}
        </ul>
      )}
    </>
  );

  if (part === 'map') {
    return (
      <div className="feature-card">
        {header}
        {zenohSession && !online && <p className="hint">Нода vision на роботе не отвечает.</p>}
        {mapPart}
      </div>
    );
  }

  return (
    <div className="feature-card">
      {header}
      {zenohSession && !online && <p className="hint">Нода vision на роботе не отвечает.</p>}
      {online && state!.asleep && <p className="hint">Робот спит: камера и распознавание выключены.</p>}
      {online && !state!.asleep && !state!.camera && <p className="error-text">Нет кадров с камеры робота.</p>}
      {state?.error && <p className="error-text">{state.error}</p>}
      {online && (
        <div className="row">
          {backendBadge('people', 'люди')}
          {backendBadge('detector', 'предметы')}
          {backendBadge('pose', 'поза')}
          {backendBadge('faces', 'лица')}
          {backendBadge('qr', 'QR')}
          {backendBadge('aruco', 'ArUco')}
        </div>
      )}

      <div className="row">
        <button type="button" className="btn-sm primary" disabled={!online}
          onClick={() => putJson(zenohSession, `${prefix}/cmd`, { cmd: 'describe', speak: !!cfg?.speak })}>
          Что в кадре?
        </button>
      </div>

      <h4>О чём сообщать</h4>
      <div className="checks">
        {TOPICS.map(([key, label]) => (
          <label key={key}>
            <input type="checkbox" checked={!!cfg?.[key]} disabled={!online}
              onChange={e => setConfig({ [key]: e.target.checked } as Partial<VisionConfig>)} />
            {label}
          </label>
        ))}
      </div>
      {cfg?.objects && (
        <details>
          <summary>Какие предметы ({chosen.size ? `выбрано ${chosen.size}` : 'все'})</summary>
          <div className="row">
            <button type="button" className="btn-sm" disabled={!chosen.size} onClick={() => setConfig({ classes: [] })}>Все</button>
            <span className="hint">Отметьте нужные — остальные не будут озвучиваться.</span>
          </div>
          <div className="class-grid">
            {Object.entries(classes).filter(([label]) => label !== 'person').map(([label, ru]) => (
              <label key={label}>
                <input type="checkbox" checked={chosen.has(label)} onChange={e => toggleClass(label, e.target.checked)} />
                {ru}
              </label>
            ))}
          </div>
        </details>
      )}

      <h4>Озвучивать и оповещать</h4>
      <div className="checks">
        <label title={speechSupported() ? '' : 'Браузер не умеет синтезировать речь'}>
          <input type="checkbox" checked={speakBrowser} disabled={!speechSupported()}
            onChange={e => { setSpeakBrowser(e.target.checked); savePref('vision.speakBrowser', e.target.checked); }} />
          в браузере
        </label>
        <label>
          <input type="checkbox" checked={!!cfg?.speak} disabled={!online} onChange={e => setConfig({ speak: e.target.checked })} />
          в колонке робота
        </label>
        <label title="Озвучивать только тревожные события (падение)">
          <input type="checkbox" checked={!!cfg?.alerts_only} disabled={!online || !cfg?.speak}
            onChange={e => setConfig({ alerts_only: e.target.checked })} />
          только тревоги
        </label>
        <label title="Звуковой сигнал в браузере при тревоге">
          <input type="checkbox" checked={beepOn} onChange={e => { setBeepOn(e.target.checked); savePref('vision.beep', e.target.checked); }} />
          сигнал при тревоге
        </label>
      </div>
      <label className="slider-row">
        <span>Не чаще раза в</span>
        <input type="range" min={0} max={30} step={1} value={cfg?.min_interval_s ?? 3} disabled={!online}
          onChange={e => setConfig({ min_interval_s: Number(e.target.value) })} />
        <span className="value">{cfg?.min_interval_s ?? 3} с</span>
      </label>

      {part === 'all' && mapPart}

      <h4>Знакомые лица</h4>
      <div className="row">
        <input type="text" value={faceName} placeholder="Имя (встаньте перед камерой)" onChange={e => setFaceName(e.target.value)}
          aria-label="Имя для лица" onKeyDown={e => e.key === 'Enter' && enroll()} />
        <button type="button" className="btn-sm primary" disabled={!online || !faceName.trim() || !backends.faces} onClick={enroll}>Запомнить лицо</button>
      </div>
      {state && Object.keys(state.faces).length > 0 ? (
        <div className="row">
          {Object.entries(state.faces).map(([name, count]) => (
            <span key={name} className="badge">
              {name} ({count})
              <button type="button" className="btn-icon" title="Забыть" aria-label={`Забыть лицо ${name}`} style={{ padding: '0 2px' }}
                onClick={() => window.confirm(`Забыть лицо «${name}»?`) && putJson(zenohSession, `${prefix}/cmd`, { cmd: 'forget_face', name })}>
                ✕
              </button>
            </span>
          ))}
        </div>
      ) : (
        <p className="hint">Снимите 3–5 кадров одного человека с разных ракурсов — узнавание будет надёжнее.</p>
      )}

      <h4>Журнал</h4>
      {events.length === 0 ? (
        <p className="hint">Событий пока нет.</p>
      ) : (
        <ul className="event-list">
          {events.map(ev => (
            <li key={ev.id} className={ev.level === 'alert' || ev.level === 'error' ? 'alert' : ev.kind === 'describe' ? 'ok' : ''}>
              <span className="time">{timeOf(ev.at)}</span>
              <span className="text">{ev.text}</span>
              {speechSupported() && <button type="button" className="btn-icon" title="Озвучить" aria-label="Озвучить" onClick={() => speak(ev.text)}>🔈</button>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default VisionPanel;
